/**
 * Guidance for every License Insights finding (spec 2026-10-25, sections 6 and 8), like finding-guidance.mjs is for
 * the security checks: who acts (a Microsoft role name the customer recognizes), the one next step shown in the row,
 * the impact, the ordered steps and two links. Links are tenant agnostic: the signed-in admin lands in their own
 * tenant. A test fails when a LIC id has no guidance (portal/backend/tests/shared-license-insights.test.mjs).
 */

export const LICENSE_FINDING_IDS = Object.freeze(['LIC-001', 'LIC-002', 'LIC-003', 'LIC-004', 'LIC-005', 'LIC-006', 'LIC-007', 'LIC-008']);

const LINKS = Object.freeze({
  licenses: { label: 'Microsoft 365 admin center: Licenses', url: 'https://admin.microsoft.com/#/licenses' },
  users: { label: 'Microsoft 365 admin center: Active users', url: 'https://admin.microsoft.com/#/users' },
  products: { label: 'Microsoft 365 admin center: Your products', url: 'https://admin.microsoft.com/#/subscriptions' },
  groupLicensing: { label: 'Microsoft Entra admin center: Licenses', url: 'https://entra.microsoft.com/#view/Microsoft_AAD_IAM/LicensesMenuBlade/~/Products' },
  learnGroupErrors: { label: 'Microsoft Learn: Resolve group license assignment problems', url: 'https://learn.microsoft.com/en-us/entra/identity/users/licensing-groups-resolve-problems' },
  learnAssign: { label: 'Microsoft Learn: Assign or unassign licenses', url: 'https://learn.microsoft.com/en-us/microsoft-365/admin/manage/assign-licenses-to-users' },
  learnRemoveSeats: { label: 'Microsoft Learn: Buy or remove licenses', url: 'https://learn.microsoft.com/en-us/microsoft-365/commerce/licenses/buy-licenses' },
  learnRenew: { label: 'Microsoft Learn: Renew Microsoft 365 for business', url: 'https://learn.microsoft.com/en-us/microsoft-365/commerce/subscriptions/renew-your-subscription' },
  learnUsage: { label: 'Microsoft Learn: Microsoft 365 active users report', url: 'https://learn.microsoft.com/en-us/microsoft-365/admin/activity-reports/active-users' },
});

const G = (owner, nextStep, impact, steps, links) => Object.freeze({ owner, nextStep, impact, steps: Object.freeze(steps), links: Object.freeze(links) });

export const LICENSE_GUIDANCE = Object.freeze({
  'LIC-001': G(
    'License Administrator',
    'Remove the licenses, or remove the people from the licensing group',
    'A disabled account cannot sign in, so its licenses are paid for and unused.',
    ['Confirm the mailbox and files are retained or exported as your policy requires.', 'Remove the licenses in Active users, or remove the person from the group that assigns them.', 'Reduce the seat count before the renewal, or give the seats to someone who needs them.'],
    [LINKS.users, LINKS.learnAssign],
  ),
  'LIC-002': G(
    'License Administrator',
    'Confirm with the manager, then remove the licenses',
    'Nobody signed in to these accounts in the period you chose, so their licenses are most likely unused.',
    ['Ask each manager whether the person still needs the account (leave, shared mailbox, service account).', 'Remove the licenses the people no longer need, or disable the account if they left.', 'Reduce the seat count before the renewal.'],
    [LINKS.users, LINKS.learnAssign],
  ),
  'LIC-003': G(
    'License Administrator',
    'Remove the standalone license; the suite already provides it',
    'Every service in these licenses is already provided by another product the same person has, so the extra license adds nothing.',
    ['Check that nobody needs a service the suite turns off for them (onGuard already compares the enabled services).', 'Remove the standalone license in Active users, or from the licensing group.', 'Reduce the standalone product\'s seat count before its renewal.'],
    [LINKS.licenses, LINKS.learnAssign],
  ),
  'LIC-004': G(
    'Billing Administrator',
    'Reduce the seat count before the renewal, or assign the seats',
    'Seats bought and never assigned are paid for every month and used by nobody.',
    ['Decide whether the seats are a planned buffer (new hires) or surplus.', 'In Your products, reduce the license quantity; most annual commitments allow a reduction only at renewal.', 'Or assign the seats to the people waiting for them.'],
    [LINKS.products, LINKS.learnRemoveSeats],
  ),
  'LIC-005': G(
    'Billing Administrator',
    'Decide before the end date: buy, or let it end and tell the people',
    'When a trial ends or a subscription lapses, the people on it lose the services it provides.',
    ['Check who uses the product and whether it earned its place.', 'Buy it in Your products before the end date, or let it end.', 'Tell the people affected what changes for them and when.'],
    [LINKS.products, LINKS.learnRenew],
  ),
  'LIC-006': G(
    'License Administrator',
    'Fix the group licensing error so the people get their services',
    'Group members whose license failed are missing the services the group should give them.',
    ['Open the group in Licenses to see the error per person.', 'Not enough seats: buy seats or remove people from the group. Conflicts or dependencies: change the other license first.', 'Reprocess the group so Microsoft assigns the licenses again.'],
    [LINKS.groupLicensing, LINKS.learnGroupErrors],
  ),
  'LIC-007': G(
    'License Administrator',
    'Review with managers; consider a smaller plan',
    'These people are licensed but used no Microsoft 365 service in the period, so a smaller plan or no license may do.',
    ['Review the list with each manager.', 'Move people who only need email or the web apps to a smaller plan, or remove the license.', 'Check the usage report again next month.'],
    [LINKS.licenses, LINKS.learnUsage],
  ),
  'LIC-008': G(
    'License Administrator',
    'Confirm with the manager, then remove the product or move to a smaller plan',
    'Microsoft reports no use of these products by these people in the period, so the licenses are most likely paid for and unused.',
    ['Review the list with each manager: someone on leave or a seasonal worker may still need the product.', 'Remove the product the person does not use, or move them to a smaller plan.', 'Reduce the seat count before the renewal.'],
    [LINKS.licenses, LINKS.learnUsage],
  ),
});

/**
 * The license best practice each check verifies (the Licenses usage report, Hugo Andrade 2026-10-30), and the words of a
 * check with nothing to do. Plain statements a customer admin recognizes; the order is the order of the report.
 */
export const LICENSE_BEST_PRACTICES = Object.freeze([
  Object.freeze({ id: 'LIC-001', practice: 'Remove licenses from disabled accounts', ok: 'No licenses on disabled accounts' }),
  Object.freeze({ id: 'LIC-002', practice: 'Reclaim licenses from accounts nobody signs in to', ok: 'No licenses on accounts without a sign-in in the period' }),
  Object.freeze({ id: 'LIC-003', practice: 'Do not pay twice for the same services', ok: 'No license duplicates another product the same person has' }),
  Object.freeze({ id: 'LIC-008', practice: 'License only the products people use', ok: 'Every product onGuard can check was used in the period' }),
  Object.freeze({ id: 'LIC-007', practice: 'Review licensed people who use no Microsoft 365 service', ok: 'Every licensed person used Microsoft 365 in the period' }),
  Object.freeze({ id: 'LIC-004', practice: 'Keep every seat you buy assigned', ok: 'Every seat bought is assigned' }),
  Object.freeze({ id: 'LIC-005', practice: 'Decide on trials and lapsing subscriptions before they end', ok: 'No trial or subscription is ending or lapsing' }),
  Object.freeze({ id: 'LIC-006', practice: 'Fix group licensing errors', ok: 'No group licensing errors' }),
]);

/** The guidance of a LIC finding, or null. */
export function licenseGuidanceFor(id) {
  return LICENSE_GUIDANCE[id] ?? null;
}

/** Plain words per Microsoft group licensing error code (spec 6, LIC-006). */
export const LICENSE_ERROR_WORDS = Object.freeze({
  CountViolation: 'not enough seats',
  MutuallyExclusiveViolation: 'conflicts with another license',
  DependencyViolation: 'needs another license first',
  ProhibitedInUsageLocationViolation: 'not available in the user\'s usage location',
  UniquenessViolation: 'duplicate proxy address',
  Other: 'Microsoft reported another error',
});

export function licenseErrorWords(code) {
  return LICENSE_ERROR_WORDS[code] ?? LICENSE_ERROR_WORDS.Other;
}
